import React, { useCallback, useEffect, useState, Children } from 'react';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
function Modal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={title} data-id="element-52">
      {/* Backdrop */}
      <div className="absolute inset-0" style={{
      backgroundColor: 'rgba(26,39,68,0.75)',
      backdropFilter: 'blur(4px)'
    }} onClick={onClose} aria-hidden="true" data-id="element-53" />

      {/* Modal card */}
      <div className="relative w-full max-w-2xl max-h-[85vh] rounded-2xl flex flex-col shadow-2xl" style={{
      backgroundColor: '#faf7f2',
      border: '1px solid rgba(201,169,110,0.2)'
    }} data-id="element-54">
        {/* Header */}
        <div className="flex items-center justify-between px-7 py-5 border-b flex-shrink-0" style={{
        borderColor: 'rgba(201,169,110,0.2)'
      }} data-id="element-55">
          <h2 className="text-xl md:text-2xl font-bold" style={{
          fontFamily: "'Playfair Display', serif",
          color: '#1a2744'
        }} data-id="element-56">
            {title}
          </h2>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400" style={{
          backgroundColor: 'rgba(26,39,68,0.08)'
        }} aria-label="Close" data-id="element-57">
            <XIcon className="w-4 h-4" style={{
            color: '#1a2744'
          }} aria-hidden="true" data-id="element-58" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto px-7 py-6 flex-1" style={{
        fontFamily: "'Inter', sans-serif",
        color: '#4a4540',
        lineHeight: '1.75'
      }} data-id="element-59">
          {children}
        </div>
      </div>
    </div>;
}
function PrivacyContent() {
  return <div className="space-y-5 text-sm md:text-base" data-id="element-60">
      <p style={{
      color: '#6b6560',
      fontSize: '0.8rem'
    }} data-id="element-61">
        Last updated: February 2026
      </p>

      <section data-id="element-62">
        <h3 className="font-bold mb-2" style={{
        fontFamily: "'Playfair Display', serif",
        color: '#1a2744'
      }} data-id="element-63">
          1. Information We Collect
        </h3>
        <p data-id="element-64">
          When you purchase The 3-Night "No-Cry" Sleep Reset, we collect only
          the information necessary to process your transaction. This may
          include your name and email address as provided through our payment
          processor. We do not collect sensitive personal information beyond
          what is required to deliver your digital product.
        </p>
      </section>

      <section data-id="element-65">
        <h3 className="font-bold mb-2" style={{
        fontFamily: "'Playfair Display', serif",
        color: '#1a2744'
      }} data-id="element-66">
          2. How We Use Your Information
        </h3>
        <p data-id="element-67">
          Your information is used solely to deliver your purchased digital
          product and to send you a receipt of your transaction. We may send you
          occasional emails related to your purchase. We do not sell, rent, or
          share your personal information with third parties for marketing
          purposes.
        </p>
      </section>

      <section data-id="element-68">
        <h3 className="font-bold mb-2" style={{
        fontFamily: "'Playfair Display', serif",
        color: '#1a2744'
      }} data-id="element-69">
          3. Cookies and Tracking
        </h3>
        <p data-id="element-70">
          This website may use cookies and similar tracking technologies to
          improve your browsing experience and to analyze website traffic. You
          may disable cookies through your browser settings; however, this may
          affect certain features of the site. We may use third-party analytics
          tools (such as Google Analytics) to understand how visitors interact
          with our site.
        </p>
      </section>

      <section data-id="element-71">
        <h3 className="font-bold mb-2" style={{
        fontFamily: "'Playfair Display', serif",
        color: '#1a2744'
      }} data-id="element-72">
          4. Payment Processing
        </h3>
        <p data-id="element-73">
          All payment transactions are processed by a secure third-party payment
          processor. We do not store or have access to your full credit card or
          payment details. Your payment information is handled in accordance
          with PCI-DSS standards by our payment provider.
        </p>
      </section>

      <section data-id="element-74">
        <h3 className="font-bold mb-2" style={{
        fontFamily: "'Playfair Display', serif",
        color: '#1a2744'
      }} data-id="element-75">
          5. Data Retention
        </h3>
        <p data-id="element-76">
          We retain your email address and purchase record for as long as
          necessary to fulfill our legal and business obligations, including
          providing customer support for your purchase. You may request deletion
          of your data at any time.
        </p>
      </section>

      <section data-id="element-77">
        <h3 className="font-bold mb-2" style={{
        fontFamily: "'Playfair Display', serif",
        color: '#1a2744'
      }} data-id="element-78">
          6. Children's Privacy
        </h3>
        <p data-id="element-79">
          This website is intended for adults (parents and caregivers). We do
          not knowingly collect personal information from children under the age
          of 13. If you believe a child has provided us with personal
          information, please contact us so we can remove it.
        </p>
      </section>

      <section data-id="element-80">
        <h3 className="font-bold mb-2" style={{
        fontFamily: "'Playfair Display', serif",
        color: '#1a2744'
      }} data-id="element-81">
          7. Security
        </h3>
        <p data-id="element-82">
          We implement reasonable security measures to protect your personal
          information from unauthorized access, disclosure, or destruction.
          However, no method of transmission over the internet is 100% secure,
          and we cannot guarantee absolute security.
        </p>
      </section>

      <section data-id="element-83">
        <h3 className="font-bold mb-2" style={{
        fontFamily: "'Playfair Display', serif",
        color: '#1a2744'
      }} data-id="element-84">
          8. Changes to This Policy
        </h3>
        <p data-id="element-85">
          We reserve the right to update this Privacy Policy at any time.
          Changes will be posted on this page with an updated date. Your
          continued use of this site after any changes constitutes your
          acceptance of the new policy.
        </p>
      </section>
    </div>;
}
function TermsContent() {
  return <div className="space-y-5 text-sm md:text-base" data-id="element-86">
      <p style={{
      color: '#6b6560',
      fontSize: '0.8rem'
    }} data-id="element-87">
        Last updated: February 2026
      </p>

      <section data-id="element-88">
        <h3 className="font-bold mb-2" style={{
        fontFamily: "'Playfair Display', serif",
        color: '#1a2744'
      }} data-id="element-89">
          1. Acceptance of Terms
        </h3>
        <p data-id="element-90">
          By purchasing and downloading The 3-Night "No-Cry" Sleep Reset, you
          agree to be bound by these Terms of Service. If you do not agree to
          these terms, please do not purchase or use this product.
        </p>
      </section>

      <section data-id="element-91">
        <h3 className="font-bold mb-2" style={{
        fontFamily: "'Playfair Display', serif",
        color: '#1a2744'
      }} data-id="element-92">
          2. Digital Product & Delivery
        </h3>
        <p data-id="element-93">
          Upon successful payment, you will receive immediate access to download
          The 3-Night "No-Cry" Sleep Reset as a PDF file. This is a digital
          product — no physical item will be shipped. You are responsible for
          ensuring your device is capable of opening PDF files.
        </p>
      </section>

      <section data-id="element-94">
        <h3 className="font-bold mb-2" style={{
        fontFamily: "'Playfair Display', serif",
        color: '#1a2744'
      }} data-id="element-95">
          3. License & Permitted Use
        </h3>
        <p data-id="element-96">
          Your purchase grants you a single, non-transferable, personal license
          to use this guide for your own household. You may not reproduce,
          distribute, resell, share, or republish any portion of this product in
          any form — digital or physical — without express written permission.
          This includes sharing via email, social media, or any file-sharing
          platform.
        </p>
      </section>

      <section data-id="element-97">
        <h3 className="font-bold mb-2" style={{
        fontFamily: "'Playfair Display', serif",
        color: '#1a2744'
      }} data-id="element-98">
          4. Refund Policy
        </h3>
        <p data-id="element-99">
          Due to the instant-access nature of digital downloads, all sales are
          final. We do not offer refunds once the product has been delivered.
          Please review all product information carefully before purchasing. If
          you experience a technical issue preventing download, please reach out
          and we will resolve it promptly.
        </p>
      </section>

      <section data-id="element-100">
        <h3 className="font-bold mb-2" style={{
        fontFamily: "'Playfair Display', serif",
        color: '#1a2744'
      }} data-id="element-101">
          5. Medical Disclaimer
        </h3>
        <p data-id="element-102">
          The information contained in this guide is for educational purposes
          only and is based on general pediatric sleep hygiene principles. It is
          not intended as medical advice and should not replace the guidance of
          a licensed healthcare provider. Always consult your child's
          pediatrician before making significant changes to their sleep routine,
          especially if your child has any medical conditions.
        </p>
      </section>

      <section data-id="element-103">
        <h3 className="font-bold mb-2" style={{
        fontFamily: "'Playfair Display', serif",
        color: '#1a2744'
      }} data-id="element-104">
          6. Results Disclaimer
        </h3>
        <p data-id="element-105">
          Individual results will vary. The outcomes described on this page
          represent typical experiences when the protocol is followed
          consistently. We make no guarantee of specific results. Success
          depends on consistent implementation of the protocol by the parent or
          caregiver.
        </p>
      </section>

      <section data-id="element-106">
        <h3 className="font-bold mb-2" style={{
        fontFamily: "'Playfair Display', serif",
        color: '#1a2744'
      }} data-id="element-107">
          7. Intellectual Property
        </h3>
        <p data-id="element-108">
          All content within this guide, including text, frameworks, protocols,
          and naming conventions (such as "The Circadian Anchor," "The Quiet
          Return," and "The Protein Bridge Protocol"), is the intellectual
          property of the creator. Unauthorized reproduction or distribution is
          a violation of copyright law.
        </p>
      </section>

      <section data-id="element-109">
        <h3 className="font-bold mb-2" style={{
        fontFamily: "'Playfair Display', serif",
        color: '#1a2744'
      }} data-id="element-110">
          8. Limitation of Liability
        </h3>
        <p data-id="element-111">
          To the maximum extent permitted by law, we shall not be liable for any
          indirect, incidental, or consequential damages arising from the use or
          inability to use this product. Our total liability shall not exceed
          the amount paid for the product.
        </p>
      </section>

      <section data-id="element-112">
        <h3 className="font-bold mb-2" style={{
        fontFamily: "'Playfair Display', serif",
        color: '#1a2744'
      }} data-id="element-113">
          9. Governing Law
        </h3>
        <p data-id="element-114">
          These terms shall be governed by and construed in accordance with
          applicable law. Any disputes arising from these terms or your purchase
          shall be resolved through good-faith negotiation before pursuing any
          other remedy.
        </p>
      </section>
    </div>;
}
export function Footer() {
  const [openModal, setOpenModal] = useState<ModalType>(null);
  const close = useCallback(() => setOpenModal(null), []);
  return <>
      <footer className="w-full py-8 px-6" style={{
      backgroundColor: '#111c35',
      borderTop: '1px solid rgba(201,169,110,0.15)'
    }} aria-label="Site footer" data-id="element-115">
        <div className="max-w-6xl mx-auto flex flex-col items-center gap-4" data-id="element-116">
          {/* Logo / brand mark */}
          <p className="text-sm font-semibold tracking-wide" style={{
          fontFamily: "'Playfair Display', serif",
          color: '#c9a96e'
        }} data-id="element-117">
            The 3-Night Sleep Reset
          </p>

          {/* Links */}
          <nav className="flex items-center gap-6" aria-label="Legal links" data-id="element-118">
            <button onClick={() => setOpenModal('privacy')} className="text-xs underline underline-offset-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400 rounded" style={{
            color: '#a09890',
            fontFamily: "'Inter', sans-serif"
          }} data-id="element-119">
              Privacy Policy
            </button>
            <span style={{
            color: 'rgba(160,152,144,0.3)'
          }} aria-hidden="true" data-id="element-120">
              •
            </span>
            <button onClick={() => setOpenModal('terms')} className="text-xs underline underline-offset-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400 rounded" style={{
            color: '#a09890',
            fontFamily: "'Inter', sans-serif"
          }} data-id="element-121">
              Terms of Service
            </button>
          </nav>

          {/* Copyright */}
          <p className="text-xs" style={{
          color: 'rgba(160,152,144,0.5)',
          fontFamily: "'Inter', sans-serif"
        }} data-id="element-122">
            © {new Date().getFullYear()} The 3-Night Sleep Reset. All rights
            reserved.
          </p>
        </div>
      </footer>

      {/* Privacy Modal */}
      <Modal isOpen={openModal === 'privacy'} onClose={close} title="Privacy Policy" data-id="element-123">
        <PrivacyContent data-id="element-124" />
      </Modal>

      {/* Terms Modal */}
      <Modal isOpen={openModal === 'terms'} onClose={close} title="Terms of Service" data-id="element-125">
        <TermsContent data-id="element-126" />
      </Modal>
    </>;
}